import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { isNativePlatform } from './utils/platform';

// In production, use the app's own domain as authDomain so that the Firebase
// sign-in helper (/__/auth/handler) is served from the same origin via a
// Vercel rewrite proxy.  This avoids cross-origin storage access which Safari
// ITP (and Firefox / Chrome) blocks, causing signInWithRedirect to silently
// fail.  In development we keep the default firebaseapp.com domain because
// localhost is not subject to the same restrictions and there is no proxy.
//
// Inside Capacitor's native WebView, window.location.hostname is "localhost"
// (or similar), which is not a valid authDomain.  Native apps use popup-based
// auth instead of redirects, so the ITP workaround is unnecessary — use the
// default Firebase authDomain.
const defaultAuthDomain = (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '').trim();
const authDomain =
  !import.meta.env.DEV && typeof window !== 'undefined' && !isNativePlatform()
    ? window.location.hostname
    : defaultAuthDomain;

const firebaseConfig = {
  apiKey: (import.meta.env.VITE_FIREBASE_API_KEY ?? '').trim(),
  authDomain,
  projectId: (import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '').trim(),
  storageBucket: (import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '').trim(),
  messagingSenderId: (import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '').trim(),
  appId: (import.meta.env.VITE_FIREBASE_APP_ID ?? '').trim(),
};

/** Whether Firebase is configured (all required env vars are present) */
export const isFirebaseConfigured =
  !!firebaseConfig.apiKey &&
  !!defaultAuthDomain &&
  !!firebaseConfig.projectId;

// Mutable references — populated by initFirebase().
// Getter functions expose the live value to hooks/callbacks after init.
const refs = { auth: null as Auth | null, db: null as Firestore | null };

export function getFirebaseAuth(): Auth | null { return refs.auth; }
export function getFirebaseDb(): Firestore | null { return refs.db; }

/** Single promise — cached so multiple callers don't re-init */
let _initPromise: Promise<{ auth: Auth | null; db: Firestore | null }> | null = null;

/**
 * Lazily initialise Firebase. Safe to call multiple times — subsequent calls
 * return the same cached promise.
 */
export function initFirebase(): Promise<{ auth: Auth | null; db: Firestore | null }> {
  if (_initPromise) return _initPromise;

  if (!isFirebaseConfigured) {
    _initPromise = Promise.resolve({ auth: null, db: null });
    return _initPromise;
  }

  _initPromise = new Promise((resolve) => {
    try {
      const app = initializeApp(firebaseConfig);

      // On native (Capacitor), getAuth() registers browserPopupRedirectResolver
      // which doesn't work inside WKWebView.  Use initializeAuth() with
      // indexedDBLocalPersistence for session persistence instead.
      refs.auth = isNativePlatform()
        ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
        : getAuth(app);

      // Try persistent cache first; fall back to in-memory if IndexedDB is unavailable
      try {
        refs.db = initializeFirestore(app, {
          localCache: persistentLocalCache({
            tabManager: persistentMultipleTabManager(),
          }),
        });
      } catch (_persistenceError) {
        refs.db = initializeFirestore(app, {
          localCache: memoryLocalCache(),
        });
      }

      // Connect to emulators in development (if running)
      if (import.meta.env.DEV) {
        const useEmulators = import.meta.env.VITE_FIREBASE_USE_EMULATORS === 'true';
        if (useEmulators) {
          connectAuthEmulator(refs.auth, 'http://localhost:9099');
          connectFirestoreEmulator(refs.db, 'localhost', 8080);
        }
      }

      resolve({ auth: refs.auth, db: refs.db });
    } catch (_error) {
      // Firebase initialization failed — fall back to local-only mode
      refs.auth = null;
      refs.db = null;
      resolve({ auth: null, db: null });
    }
  });

  return _initPromise;
}
